import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { normalizeColor, colorMediaIndices, prepareColorGallery } from './media-gallery-color.js';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    this.#controller = new AbortController();
    this.#initializeColorGallery();
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();
  #colorTemplate;
  #colorMedia;
  #color;
  #request = 0;

  updatedCallback() {
    // Featured products can be morphed in place instead of disconnected/reconnected.
    this.#colorTemplate = undefined;
    this.#color = undefined;
    this.#initializeColorGallery();
    super.updatedCallback();
  }

  #initializeColorGallery() {
    if (!this.dataset.colorOption || this.#colorTemplate) return;
    const data = this.querySelector('script[data-color-media]');
    if (!data) return;
    try {
      this.#colorMedia = JSON.parse(data.textContent);
      this.#colorTemplate = document.createElement('template');
      this.#colorTemplate.innerHTML = this.innerHTML;
      this.#applyColor(this.dataset.selectedColor);
    } catch (error) {
      this.#colorTemplate = undefined;
      console.warn('[media-gallery] Color mapping unavailable; keeping normal gallery.', error);
    }
  }

  #applyColor(color) {
    if (!this.#colorTemplate || this.#color === normalizeColor(color)) return;
    const fragment = this.#colorTemplate.content.cloneNode(true);
    prepareColorGallery(fragment, colorMediaIndices(this.#colorMedia, color));
    // Reuse Horizon's slideshow, grid and zoom markup; their normal lifecycle handles
    // swipe, playback, observers and active controls after an atomic color change.
    for (const selector of ['slideshow-component', '.media-gallery__grid', 'zoom-dialog']) {
      const current = this.querySelector(selector);
      const next = fragment.querySelector(selector);
      if (current && next) current.replaceWith(next);
    }
    this.#color = normalizeColor(color);
    this.dataset.selectedColor = color ?? '';
    super.updatedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const picker = event.target.closest('variant-picker');
    if (picker && picker.dataset.productId !== this.dataset.productId) return;
    if (event.target.closest('.shopify-section, dialog') !== this.closest('.shopify-section, dialog')) return;
    const request = ++this.#request;
    // The native event fires synchronously, before its section-rendering promise resolves.
    const selected = picker?.getAllSelectedOptions?.().find(
      (option) => normalizeColor(option.name) === normalizeColor(this.dataset.colorOption)
    );
    if (selected) this.#applyColor(selected.value);

    event.promise
      .then(({ detail }) => {
        if (!detail?.html || !this.isConnected || request !== this.#request) return;

        const { html } = detail;
        const newMediaGallery = [...html.querySelectorAll('media-gallery')].find(
          (gallery) => gallery.dataset.galleryKey === this.dataset.galleryKey
        );
        if (!newMediaGallery) return;

        // Same product already updated locally. Size/availability responses must not
        // reset the current color or replace it with Shopify's fallback variant.
        if (this.#colorTemplate && newMediaGallery.dataset.productId === this.dataset.productId && selected) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
